import {
  FirestoreDataConverter as AdminFirestoreDataConverter,
  DocumentData,
  WithFieldValue,
} from 'firebase-admin/firestore';

import { FirestoreDataConverter, SetOptions, QueryDocumentSnapshot } from 'firebase/firestore';

export type FirestoreConverter<T> = AdminFirestoreDataConverter<T> & FirestoreDataConverter<T>;

export type FirestoreNestedConverter<T> = {
  fromFirestore?: (snapshot: QueryDocumentSnapshot<T>) => T;
  toFirestore?: (modelObject: WithFieldValue<T>, options?: SetOptions) => DocumentData;
};

/**
 * `FirestoreConverter<T>` is the intersection of the client and Admin SDK converter types. That
 * intersection breaks `withConverter()`'s inference on the Admin SDK: `data()` comes back as
 * `WithFieldValue<T>` and every call site ends up casting it by hand. Narrow the converter to the
 * Admin SDK shape once here instead of duplicating the cast at each backend converter.
 */
export const asAdminConverter = <T>(
  converter: FirestoreConverter<T>,
): AdminFirestoreDataConverter<T> => converter as unknown as AdminFirestoreDataConverter<T>;

/** El mismo problema del lado del cliente: sin esto, `data()` llega como `WithFieldValue<T>`. */
export const asClientConverter = <T>(converter: FirestoreConverter<T>): FirestoreDataConverter<T> =>
  converter as unknown as FirestoreDataConverter<T>;

export const withNestedConverter = <T>(firestoreConverter: FirestoreConverter<T>) => {
  return (nestedConverter?: FirestoreNestedConverter<T>): FirestoreConverter<T> => {
    const converter: FirestoreConverter<T> = {
      toFirestore(documentData: WithFieldValue<T>, options?: SetOptions) {
        const convertedData = firestoreConverter.toFirestore(documentData, options);

        if (nestedConverter && nestedConverter.toFirestore) {
          return nestedConverter.toFirestore(convertedData as WithFieldValue<T>);
        }

        return convertedData;
      },
      fromFirestore(snapshot) {
        const convertedData = firestoreConverter.fromFirestore(snapshot);

        if (nestedConverter && nestedConverter.fromFirestore) {
          const modifiedSnapshot: QueryDocumentSnapshot<T> = {
            ...snapshot,
            data: () => convertedData,
          };

          return nestedConverter.fromFirestore(modifiedSnapshot);
        }

        return convertedData;
      },
    };

    return converter;
  };
};
